import {
  createSettingsParam,
  createSettingsParams,
  SettingsParamType,
} from '@wix/tpa-settings';
import {
  AlignmentOptions,
  GalleryArrowPositionOptions,
  GalleryItemSizeOptions,
  GalleryLayoutOptions,
  ImagePositionOptions,
  ImageResizeOptions,
  ISection,
  SectionTypes,
  SidebarPosition,
} from './types';
import { AccessibilityHtmlTags } from '../../utils/AccessibilityHtmlTags.const';

export const defaultSections: ISection[] = [
  {
    type: SectionTypes.TITLE_TAGLINE,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.DETAILS,
    visible: true,
    bookButton: true,
  },
  {
    type: SectionTypes.DESCRIPTION,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.GALLERY,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.SCHEDULING,
    visible: true,
    bookButton: true,
  },
  {
    type: SectionTypes.POLICY,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.CONTACT,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.PLUGIN,
    visible: true,
    bookButton: false,
  },
];

interface DesktopSettingsKeys {
  policySectionTitle: SettingsParamType.Text;
  sections: ISection[];
  headerVisibility: SettingsParamType.Boolean;
  headerTitleVisibility: SettingsParamType.Boolean;
  headerTitleAlignment: AlignmentOptions;
  headerImageVisibility: SettingsParamType.Boolean;
  headerImagePosition: ImagePositionOptions;
  headerBookButtonVisibility: SettingsParamType.Boolean;
  sidebarVisibility: SettingsParamType.Boolean;
  onlineBadgeVisibility: SettingsParamType.Boolean;
  onlineBadgeText: SettingsParamType.Text;
  serviceTitleVisibility: SettingsParamType.Boolean;
  serviceTaglineVisibility: SettingsParamType.Boolean;
  detailsDurationVisibility: SettingsParamType.Boolean;
  detailsPriceVisibility: SettingsParamType.Boolean;
  detailsLocationVisibility: SettingsParamType.Boolean;
  detailsBoxAlignment: AlignmentOptions | undefined;
  detailsTextAlignment: AlignmentOptions | undefined;
  detailsButtonAlignment: AlignmentOptions | undefined;
  titleAndTaglineAlignment: AlignmentOptions | undefined;
  businessEmailVisibility: SettingsParamType.Boolean;
  businessPhoneNumberVisibility: SettingsParamType.Boolean;
  businessAddressVisibility: SettingsParamType.Boolean;
  locationNameVisibility: SettingsParamType.Boolean;
  contactDetailsTitleText: SettingsParamType.Text;
  descriptionTitleText: SettingsParamType.Text;
  bookButtonText: SettingsParamType.Text;
  pendingApprovalButtonText: SettingsParamType.Text;
  serviceUnavailableMessageText: SettingsParamType.Text;
  availabilityMessageText: SettingsParamType.Text;
  numberOfSpotsLeftMessageText: SettingsParamType.Text;
  fullCourseMessageText: SettingsParamType.Text;
  unBookableCourseMessageText: SettingsParamType.Text;
  bookStartedCourseMessageText: SettingsParamType.Text;
  sidebarSection: SectionTypes;
  sidebarFreezePosition: SettingsParamType.Boolean;
  sidebarPosition: SidebarPosition;
  sidebarAlignment: AlignmentOptions;
  bodyAlignment: AlignmentOptions;
  columnPosition: AlignmentOptions;
  scheduleDays: SettingsParamType.Number;
  scheduleSectionTitle: SettingsParamType.Text;
  scheduleLoadAllText: SettingsParamType.Text;
  scheduleEmptyStateText: SettingsParamType.Text;
  scheduleDurationVisibility: SettingsParamType.Boolean;
  scheduleStaffVisibility: SettingsParamType.Boolean;
  showSessionLimit: SettingsParamType.Boolean;
  scheduleSessionLimit: SettingsParamType.Number;
  headerTitleHtmlTag: SettingsParamType.String;
  bodyServiceTitleHtmlTag: SettingsParamType.String;
  bodySectionTitleHtmlTag: SettingsParamType.String;
  sidebarServiceTitleHtmlTag: SettingsParamType.String;
  sidebarTitleHtmlTag: SettingsParamType.String;
  galleryLayout: GalleryLayoutOptions;
  galleryMasonryItemSpacing: SettingsParamType.Number;
  galleryMasonryItemBorderWidth: SettingsParamType.Number;
  galleryMasonryItemRadius: SettingsParamType.Number;
  galleryMasonryItemSize: GalleryItemSizeOptions;
  galleryGridItemSpacing: SettingsParamType.Number;
  galleryGridItemBorderWidth: SettingsParamType.Number;
  galleryGridItemRadius: SettingsParamType.Number;
  galleryGridThumbnailSize: SettingsParamType.Number;
  gallerySliderItemSpacing: SettingsParamType.Number;
  gallerySliderItemBorderWidth: SettingsParamType.Number;
  gallerySliderItemRadius: SettingsParamType.Number;
  gallerySliderImageResizeOption: ImageResizeOptions;
  gallerySliderLoopImages: SettingsParamType.Boolean;
  gallerySliderAutoSlide: SettingsParamType.Boolean;
  gallerySliderTimeBetweenImages: SettingsParamType.Number;
  gallerySliderShowArrows: SettingsParamType.Boolean;
  gallerySliderArrowsSize: SettingsParamType.Number;
  gallerySliderArrowsPosition: GalleryArrowPositionOptions;
  gallerySliderHeight: SettingsParamType.Number;
  buttonsFullWidth: SettingsParamType.Boolean;
  courseAvailability: SettingsParamType.Boolean;
  displayNumberOfSpots: SettingsParamType.Boolean;
}

interface MobileSettingsKeys {
  mobileSections: SectionTypes[] | undefined;
}

export const uninitializedVisibilitySettingsFalseValue = -1 as any as boolean;
export const uninitializedVisibilitySettingsTrueValue = -2 as any as boolean;

const scheduleDays = createSettingsParam('scheduleDays', {
  getDefaultValue: () => 7,
  type: SettingsParamType.Number,
});

export default createSettingsParams<DesktopSettingsKeys & MobileSettingsKeys>({
  sections: {
    type: SettingsParamType.Object,
    getDefaultValue: () => defaultSections,
  },
  headerVisibility: {
    getDefaultValue: () => true,
  },
  headerTitleVisibility: {
    getDefaultValue: () => false,
  },
  headerTitleAlignment: {
    getDefaultValue: (): AlignmentOptions => AlignmentOptions.CENTER,
  },
  headerImageVisibility: {
    getDefaultValue: () => true,
  },
  headerImagePosition: {
    getDefaultValue: () => ImagePositionOptions.MIDDLE,
  },
  headerBookButtonVisibility: {
    getDefaultValue: () => false,
  },
  sidebarVisibility: {
    getDefaultValue: () => false,
  },
  sidebarSection: {
    getDefaultValue: () => SectionTypes.CONTACT,
  },
  sidebarFreezePosition: {
    getDefaultValue: () => false,
  },
  sidebarPosition: {
    getDefaultValue: ({ isRTL }) =>
      isRTL ? SidebarPosition.LEFT : SidebarPosition.RIGHT,
  },
  sidebarAlignment: {
    getDefaultValue: ({ isRTL }): AlignmentOptions =>
      isRTL ? AlignmentOptions.RIGHT : AlignmentOptions.LEFT,
  },
  onlineBadgeVisibility: {
    getDefaultValue: () => true,
  },
  serviceTitleVisibility: {
    getDefaultValue: () => true,
  },
  serviceTaglineVisibility: {
    getDefaultValue: () => true,
  },
  titleAndTaglineAlignment: {
    getDefaultValue: () => undefined,
  },
  policySectionTitle: {
    getDefaultValue: ({ t }) => t('app.settings.sections.policy.default.title'),
    type: SettingsParamType.Text,
  },
  detailsDurationVisibility: {
    getDefaultValue: () => true,
  },
  detailsPriceVisibility: {
    getDefaultValue: () => true,
  },
  detailsLocationVisibility: {
    getDefaultValue: () => true,
  },
  detailsBoxAlignment: {
    getDefaultValue: () => undefined,
  },
  detailsTextAlignment: {
    getDefaultValue: () => undefined,
  },
  detailsButtonAlignment: {
    getDefaultValue: () => undefined,
  },
  businessEmailVisibility: {
    getDefaultValue: () => true,
  },
  businessPhoneNumberVisibility: {
    getDefaultValue: () => true,
  },
  businessAddressVisibility: {
    getDefaultValue: () => true,
  },
  locationNameVisibility: {
    getDefaultValue: () => false,
  },
  contactDetailsTitleText: {
    getDefaultValue: ({ t }) =>
      t('app.settings.sections.contact.default.contact-details-title.label'),
    type: SettingsParamType.Text,
  },
  descriptionTitleText: {
    getDefaultValue: ({ t }) => t('app.description.title'),
    type: SettingsParamType.Text,
  },
  onlineBadgeText: {
    getDefaultValue: ({ t }) =>
      t('app.settings.sections.titles.default.online-badge.text'),
    type: SettingsParamType.Text,
  },
  bookButtonText: {
    getDefaultValue: ({ t }) => t('app.widget.cta.book-now'),
    type: SettingsParamType.Text,
  },
  pendingApprovalButtonText: {
    getDefaultValue: ({ t }) => t('app.widget.cta.pending-approval'),
    type: SettingsParamType.Text,
  },
  serviceUnavailableMessageText: {
    getDefaultValue: ({ t }) => t('app.widget.uou-messages.book-not-available'),
    type: SettingsParamType.Text,
  },
  availabilityMessageText: {
    getDefaultValue: ({ t }) => t('service.course.available-spots'),
    type: SettingsParamType.Text,
  },
  numberOfSpotsLeftMessageText: {
    getDefaultValue: () => '',
    type: SettingsParamType.Text,
  },
  fullCourseMessageText: {
    getDefaultValue: ({ t }) => t('app.widget.uou-messages.fully-booked'),
    type: SettingsParamType.Text,
  },
  unBookableCourseMessageText: {
    getDefaultValue: ({ t }) =>
      t('app.widget.uou-messages.book-already-closed'),
    type: SettingsParamType.Text,
  },
  bookStartedCourseMessageText: {
    getDefaultValue: ({ t }) =>
      t('app.widget.uou-messages.book-available-when-service-started'),
    type: SettingsParamType.Text,
  },
  bodyAlignment: {
    getDefaultValue: ({ isRTL }): AlignmentOptions =>
      isRTL ? AlignmentOptions.RIGHT : AlignmentOptions.LEFT,
  },
  columnPosition: {
    getDefaultValue: (): AlignmentOptions => AlignmentOptions.CENTER,
  },
  scheduleDays,
  scheduleSectionTitle: {
    getDefaultValue: ({ t }) => t('app.scheduling.title.default'),
    type: SettingsParamType.Text,
  },
  scheduleLoadAllText: {
    getDefaultValue: ({ t }) => t('app.scheduling.load-all-sessions.default'),
    type: SettingsParamType.Text,
  },
  scheduleEmptyStateText: {
    getDefaultValue: ({ t, getSettingParamValue }) =>
      t('app.scheduling.empty-state', {
        daysAhead: getSettingParamValue(scheduleDays),
      }),
    type: SettingsParamType.Text,
  },
  scheduleDurationVisibility: {
    getDefaultValue: () => true,
  },
  scheduleStaffVisibility: {
    getDefaultValue: () => true,
  },
  showSessionLimit: {
    getDefaultValue: () => true,
  },
  scheduleSessionLimit: {
    getDefaultValue: () => 3,
  },
  headerTitleHtmlTag: {
    getDefaultValue: () => AccessibilityHtmlTags.MainHeader,
  },
  sidebarServiceTitleHtmlTag: {
    getDefaultValue: () => AccessibilityHtmlTags.MainHeader,
  },
  bodyServiceTitleHtmlTag: {
    getDefaultValue: () => AccessibilityHtmlTags.MainHeader,
  },
  bodySectionTitleHtmlTag: {
    getDefaultValue: () => AccessibilityHtmlTags.SecondaryHeader,
  },
  sidebarTitleHtmlTag: {
    getDefaultValue: () => AccessibilityHtmlTags.SecondaryHeader,
  },
  galleryLayout: {
    getDefaultValue: () => GalleryLayoutOptions.GRID,
  },
  galleryMasonryItemSize: {
    getDefaultValue: () => GalleryItemSizeOptions.MEDIUM,
  },
  galleryMasonryItemSpacing: {
    getDefaultValue: () => 12,
    inheritDesktop: false,
  },
  galleryMasonryItemBorderWidth: {
    getDefaultValue: () => 0,
  },
  galleryMasonryItemRadius: {
    getDefaultValue: () => 0,
  },
  galleryGridThumbnailSize: {
    getDefaultValue: ({ isMobile }) => (isMobile ? 200 : 280),
    inheritDesktop: false,
  },
  galleryGridItemSpacing: {
    getDefaultValue: () => 12,
    inheritDesktop: false,
  },
  galleryGridItemBorderWidth: {
    getDefaultValue: () => 0,
  },
  galleryGridItemRadius: {
    getDefaultValue: () => 0,
  },
  gallerySliderItemSpacing: {
    getDefaultValue: () => 12,
    inheritDesktop: false,
  },
  gallerySliderItemBorderWidth: {
    getDefaultValue: () => 0,
  },
  gallerySliderItemRadius: {
    getDefaultValue: () => 0,
  },
  gallerySliderImageResizeOption: {
    getDefaultValue: () => ImageResizeOptions.FIT,
  },
  gallerySliderTimeBetweenImages: {
    getDefaultValue: () => 4,
  },
  gallerySliderLoopImages: {
    getDefaultValue: () => true,
  },
  gallerySliderAutoSlide: {
    getDefaultValue: () => false,
  },
  gallerySliderShowArrows: {
    getDefaultValue: () => true,
  },
  gallerySliderArrowsSize: {
    getDefaultValue: ({ isMobile }) => (isMobile ? 18 : 16),
    inheritDesktop: false,
  },
  gallerySliderArrowsPosition: {
    getDefaultValue: ({ isMobile }) =>
      isMobile
        ? GalleryArrowPositionOptions.INSIDE
        : GalleryArrowPositionOptions.OUTSIDE,
    inheritDesktop: false,
  },
  gallerySliderHeight: {
    getDefaultValue: ({ isMobile }) => (isMobile ? 200 : 400),
    inheritDesktop: false,
  },
  buttonsFullWidth: {
    getDefaultValue: () => false,
    inheritDesktop: false,
  },
  mobileSections: {
    type: SettingsParamType.Object,
    getDefaultValue: () => undefined,
  },
  courseAvailability: {
    getDefaultValue: () => true,
  },
  displayNumberOfSpots: {
    getDefaultValue: () => false,
  },
});
